import { Option } from "@components/form/Dropdown.component";
import DagDataLibrary from "@components/nav/DagDataLibrary.component";
import PipelineOrchestration from "@components/orchestration/PipelineOrchestration.component";
import PipelineNodeColumnSelector from "@components/pipelineNodes/PipelineNodeColumnSelector.component";
import PipelineNodeDataTable from "@components/pipelineNodes/PIpelineNodeDataTable";
import PipelineNodeInfo from "@components/pipelineNodes/PipelineNodeInfo.component";
import PipelineNodeNotFound from "@components/pipelineNodes/PipelineNodeNotFound.component";
import PipelineNodeSubnav from "@components/pipelineNodes/PipelineNodeSubnav.component";
import SourceSelector from "@components/sources/SourceSelector.component";
import BuildOrchestrationORM, { BuildExecution, BuildOrchestration } from "@models/buildOrchestration";
import { DataWhitelist, FilterConfig, PipelineNodeField, PipelineNodeORM } from "@models/pipelineNode";
import { ColumnSort } from "@models/pipelineNode";
import PageStructure, { PageContent, PageContentInner, PageSidebar, Pane, PaneContent, PaneContentWithSubnav } from "@pages/PageStructure.component";
import FilterConfigForm from "@pages/SourceRecordType/FilterConfigForm.component";
import DefaultSortForm from "@pages/SourceRecordType/DefaultSortForm.component";
import { requireConfirmation } from "@services/alert/alert.service";
import { ApiError } from "@services/api/api.service";
import { getErrorMessage } from "@services/errors.service";
import { getNodeTypeConfig } from "@services/modeling.service";
import toast from "@services/toast.service";
import { invalidateEverything, useIsInDraftMode, useOutputTableForNode, usePipelineNode } from "@stores/data.store";
import useGlobalState from "@stores/global.state";
import { useCallback, useEffect, useMemo, useState, KeyboardEvent } from "react";
import { ButtonGroup, Form, Dropdown as BSDropdown, Button, Offcanvas, OffcanvasBody, Modal } from "react-bootstrap";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PipelineNodeColumnOrder from "@components/pipelineNodes/PipelineNodeColumnOrder.component";
import { DraftModeRequired } from "@components/project/DraftModeRequired.component";
import DataWhitelistForm from "@components/pipelineNodes/PipelineNodeDataWhitelist.component";
import PipelineNodeExtrasMenu from "@components/pipelineNodes/PipelineNodeExtrasMenu.component";
import LoadingCard from "@components/card/LoadingCard.component";
import PipelineNodeHelper from "@components/pipelineNodes/PipelineNodeHelper.component";
import BuildNodeModal from "@components/pipelineNodes/modal/BuildNodeModal.component";
import { triggerBuild } from "@services/alert/BuildModal.component";


const PipelineNodeDataPage = () => {
    const { pipelineNodeId } = useParams();
    const pipelineNode = usePipelineNode(pipelineNodeId as string);

    const [searchParams, setSearchParams] = useSearchParams();

    const query = searchParams.get('q') || '';

    const [searchInput, setSearchInput] = useState(query);

    const [fields, setFields] = useState<PipelineNodeField[]>([]);

    useEffect(() => {
        

        if (pipelineNode.data && pipelineNode.data.default_sort) {
            setDefaultSorts(pipelineNode.data.default_sort);
        }else{
            setDefaultSorts([]);
        }

        if (pipelineNode.data && pipelineNode.data.fields) {
            setFields(pipelineNode.data.fields);
        }else{
            setFields([]);
        }

        
    }, [pipelineNode.dataUpdatedAt]);

    const navigate = useNavigate();

    const [building, setBuilding] = useState(false);
    

    const [defaultSorts, setDefaultSorts] = useState<ColumnSort[]>([]);
    
    useEffect(() => {
        setSearchInput(query);
    }, [query]);

    const isInDraft = useIsInDraftMode();

    const nodeTypeConfig = useMemo(() => {
        if (!pipelineNode.data) {
            return;
        }

        return getNodeTypeConfig(pipelineNode.data);
    }, [pipelineNode.dataUpdatedAt]);

    const outputTable = useOutputTableForNode(pipelineNodeId);

    const inDraftMode = useIsInDraftMode();

    const [showConfig, setShowConfig] = useState(false);
    const [showColumnOrder, setShowColumnOrder] = useState(false);

    const checkForSearchEnterKey = useCallback((evt: KeyboardEvent<HTMLInputElement>) => {
        if (evt.keyCode == 13) {
            setSearchParams({q: searchInput});
        }
    }, [searchInput]);

    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [downloadLocation, setDownloadLocation] = useState('');
    const [downloadError, setDownloadError] = useState('');
    const [showSearchModal, setShowSearchModal] = useState(false);

    const downloadNodeData = useCallback(async () => {
        if (!pipelineNode.data) {
            return;
        }
        setDownloadError('');
        setShowDownloadModal(true);
        setLoadingDownload(true);
        setDownloadLocation('');

        try {
            const download = await PipelineNodeORM.queueDownloadJob(pipelineNodeId as string, searchInput || '');
            setDownloadLocation(download.url);
            
        } catch(err_msg) {
            setDownloadError(getErrorMessage(err_msg));
            console.log(err_msg)
        } finally {
            setLoadingDownload(false);
        }
        
    }, [pipelineNode.data, pipelineNodeId, searchInput]);

    const [showBuildNodeModal, setShowBuildNodeModal] = useState(false);

    if (pipelineNode.status == 'error' && (pipelineNode.error as ApiError).code == 404) {
        return <PipelineNodeNotFound/>
    }
    return (
        <>
            <BuildNodeModal 
                show={showBuildNodeModal} 
                onClose={() => setShowBuildNodeModal(false)}
                defaultCheck={pipelineNode.data?.node_type == 'SOURCE'}
                defaultUpstream={pipelineNode.data?.node_type != 'SOURCE'}
                header={`Refresh Data: ${pipelineNode.data?.label || ''}`}
                onConfirm={(selector: "THIS" | "UPSTREAM" | "DOWNSTREAM" | "ALL", force: boolean, check_for_records: boolean) => {triggerBuild(pipelineNode.data!, selector, force, check_for_records, () => {
                    navigate(`/node/${pipelineNodeId}`);
                })}}
            />
                <Modal show={showDownloadModal} onHide={() => setShowDownloadModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Download</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center">
                        {loadingDownload && (
                            <div>
                                <LoadingCard action="Preparing your download" />
                            </div>
                        )}
                        {!loadingDownload && downloadError && (
                            <div className="alert alert-danger">{downloadError}</div>
                        )}
                        {!loadingDownload && downloadLocation && !downloadError && (
                            <div>
                                <span style={{ fontSize: 55 }}><i className="mdi mdi mdi-download"></i></span>
                                <br />
                                <a target="_blank" className="btn btn-pliable" href={downloadLocation} rel="noreferrer">Click here to download your file.</a>
                            </div>
                        )}
                    </Modal.Body>
                </Modal>
                <Modal show={showSearchModal} onHide={() => setShowSearchModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Search</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <input
                            type="text"
                            className="form-control"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    checkForSearchEnterKey(e);
                                    setShowSearchModal(false);
                                }
                            }}
                        />
                        <Form.Text>You can use free-text search or Lucene syntax.</Form.Text>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="btn btn-light me-1" onClick={() => setShowSearchModal(false)}>Cancel</button>
                        <button className="btn btn-primary" onClick={() => {
                            setSearchParams({q: searchInput});
                            setShowSearchModal(false);
                        }}>Search</button>
                    </Modal.Footer>
                </Modal>
                <PipelineNodeSubnav pipelineNodeId={pipelineNodeId as string}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end'}}>
                        <button className="btn btn-pliable me-1" title="Refresh Data" onClick={() => setShowBuildNodeModal(true)}>
                            <i className="mdi mdi-refresh"></i>
                        </button>
                        <button className="btn btn-outline-primary me-1" onClick={() => setShowSearchModal(true)}>
                            <i className="mdi mdi-magnify"></i>
                        </button>
                        <button className="btn btn-outline-primary" onClick={downloadNodeData}>
                            <i className="mdi mdi-download"></i>
                        </button>
                        
                        
                    </div>
                </PipelineNodeSubnav>
                <PageContentInner hasHeader noScroll>
                    
    
                    <Pane>
                        <PaneContent noScroll className="pane-content">
                            <PipelineNodeDataTable
                                    defaultSorts={defaultSorts}
                                    pipelineNodeId={pipelineNodeId as string}
                                    showLineageButton
                                    searchQuery={query}
                                />
                        </PaneContent>
                    </Pane>
                </PageContentInner>
            </>
    );

}

export default PipelineNodeDataPage;